import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { authSession, logOut, getRole } from "./auth.service";
import { Role } from "types/Role";
import {
  getRouteCompanyId,
  isBareForbiddenError,
  isMembershipDisabledError,
  isNoActiveMembershipError,
  leaveCompany,
} from "./access-lost";

// Define the state type
interface AuthState {
  isLoading: boolean;
  isError: boolean;
  errorMessage: string | null;
  session: any;
  userMenuActive: boolean;
  navActive: boolean;
  role: Role | null;
  isLoggedIn: boolean;
  language: string;
  // Set when the server refused the session because every membership of the
  // person is switched off; the login page then explains why they were signed out.
  hasNoActiveMembership: boolean;
}

// Define the initial state
const initialState: AuthState = {
  isLoading: false,
  isError: false,
  errorMessage: null,
  session: null,
  userMenuActive: false,
  navActive: false,
  role: null,
  isLoggedIn: false,
  language: "lt",
  hasNoActiveMembership: false,
};

// Create the slice
const AuthSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setSession(state, action: PayloadAction<{ session: any }>) {
      // Replace 'any' with the actual session type if known
      state.session = action.payload.session;
      state.isLoggedIn = true;
      state.isLoading = false;
      state.hasNoActiveMembership = false;
    },
    setLoading(state) {
      state.isLoading = true;
      state.isError = false;
    },
    setError(state, action: PayloadAction<string>) {
      state.isLoading = false;
      state.session = null;
      state.isError = true;
      state.isLoggedIn = false;
      state.errorMessage = action.payload;
    },
    setNoActiveMembership(state) {
      state.hasNoActiveMembership = true;
    },
    setLogout(state) {
      state.isLoading = false;
      state.session = null;
      state.isLoggedIn = false;
      state.role = null;
    },
    setLanguage(state, action) {
      state.language = action.payload;
    },
    setUserMenuActive(state) {
      state.userMenuActive = !state.userMenuActive;
    },
    setNavActive(state) {
      state.navActive = !state.navActive;
    },
    setRole(state, action: PayloadAction<{ role: Role }>) {
      // Adjust the role type if necessary
      state.role = action.payload.role;
    },
  },
  /* eslint-enable */
});

// Extract the action creators object and the reducer
const { actions, reducer } = AuthSlice;

// Extract and export each action creator by name
export const {
  setSession,
  setLoading,
  setError,
  setNoActiveMembership,
  setLogout,
  setUserMenuActive,
  setNavActive,
  setRole,
} = actions;
// Export the reducer, either as a default or named export
export default reducer;

export const toogleUserMenu = () => async (dispatch: any) => {
  dispatch(setUserMenuActive());
};

export const toogleNav = () => async (dispatch: any) => {
  dispatch(setNavActive());
};

export const checkSession = () => async (dispatch: any) => {
  dispatch(setLoading());
  try {
    const session = await authSession();
    dispatch(setSession({ session }));
    return true;
  } catch (e) {
    if (isNoActiveMembershipError(e)) {
      // Nothing to open anywhere: end the server session too, and let the login page say why.
      dispatch(setNoActiveMembership());
      await dispatch(logout());
      return false;
    }
    dispatch(setError("403"));
    return false;
  }
};

export const logout = () => async (dispatch: any) => {
  dispatch(setLoading());
  try {
    await logOut();
    dispatch(setLogout());
  } catch (e: any) {
    dispatch(setError(e.message));
  }
};

export const fetchRole = (companyId: string) => async (dispatch: any) => {
  dispatch(setLoading());
  // A late answer for a company the page has already switched away from must neither replace
  // the role of the company the person is looking at now nor move them off it.
  const isForAnotherCompanyNow = () => {
    const routeCompanyId = getRouteCompanyId(window.location.pathname);
    return !!routeCompanyId && routeCompanyId !== companyId;
  };
  try {
    const role = await getRole(companyId);
    if (isForAnotherCompanyNow()) return;
    dispatch(setRole({ role }));
  } catch (e: any) {
    if (isForAnotherCompanyNow()) return;
    // The person is out of this company only; they may work in others, so the session stays.
    if (isMembershipDisabledError(e)) {
      leaveCompany("removed");
      return;
    }
    // A bare 403 means "not a member of this company", but also "not logged in": ask the
    // session. If it is fine, the person is only out of this company. If not, checkSession
    // has already ended the session.
    if (isBareForbiddenError(e)) {
      const isSessionValid = await dispatch(checkSession());
      if (isSessionValid) leaveCompany("none");
      return;
    }
    dispatch(setError(e.message));
  }
};
